/**
 * @component OrderCreateDialog
 * @description
 * A modal dialog used to create a new customer order for a selected year.
 *
 * It allows the user to:
 * - Select a **calendar year** (current or next). The next year is only available once Union
 *   has opened it for planning (GET /planning-years, SIB-928); 2027 opened on 1 October 2026.
 * - View static **Auftraggeber** (Union Investment - the customer placing the order).
 * - Choose **Leistungsempfänger** (bank - the service provider).
 * - Define a **Zeitraum (from–to)** with day/month pickers.
 * - Enter positive numeric values for **TOJ**, **Beratung**, and **Schulung**.
 *
 * Includes:
 * - Full **Yup validation** via Formik.
 * - Automatic disabling of the submit button until all required conditions are met.
 * - Validation that the end date is after the start date.
 * - Warning message if entered values exceed available capacities.
 * - Success toast confirming submission and user instructions for tracking order status.
 * - Disabled next-year button, with a tooltip naming its unlock date, until that year is open.
 *
 * @prop {Capacity} initialCapacity - The available capacity (TOJ, Beratung, Schulung) for the selected year.
 * @prop {Bank[]} banks - List of available customers (Union Investment with associated bank entities).
 * @prop {Customer[]} customers - List of bank service providers (Leistungsempfänger).
 * @prop {boolean} [isOpen] - Controls whether the dialog is open.
 * @prop {(open: boolean) => void} [onOpenChange] - Callback fired when the dialog open state changes.
 * @prop {React.ReactNode} [trigger] - Optional React element that opens the dialog.
 * @prop {(data: ActivityPayload) => Promise<{ ok: true } | { ok: false; fieldErrors?: Partial<Record<keyof ActivityPayload, string>>; message?: string }>} onSubmit
 * Callback fired when the form is submitted successfully.
 *
 * @example
 * ```tsx
 * <OrderCreateDialog
 *   initialCapacity={{ toj: 320, beratung: 120, schulung: 40 }}
 *   banks={[{
 *     id: "1",
 *     name: "Union Investment",
 *     associatedEntities: [
 *       { id: 1, name: "Finovia Bank AG" },
 *       { id: 2, name: "Alpha Retail GmbH" }
 *     ]
 *   }]}
 *   customers={[
 *     { id: 1, name: "Finovia Bank AG" },
 *     { id: 2, name: "Alpha Retail GmbH" },
 *   ]}
 *   trigger={<Button>Neue Bestellung</Button>}
 *   onSubmit={async (data) => console.log("Submit:", data)}
 * />
 * ```
 */

import { toast } from '@/components/ui/toaster/toasterHelpers'
import { Tooltip } from '@/components/ui/tooltip'
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CloseButton,
  Combobox,
  createListCollection,
  Dialog,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
  FieldRoot,
  FileUpload,
  Flex,
  HStack,
  Input,
  Portal,
  Separator,
  SelectContent,
  SelectControl,
  SelectIndicator,
  SelectIndicatorGroup,
  SelectItem,
  SelectPositioner,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
  Text,
  VStack,
  Stack,
  Stat,
} from '@chakra-ui/react'
import { usePlanningYears } from '@/hooks/usePlanningYears'
import { formatIsoDateDE } from '@/helper/valueChange.helper'
import { useFormik } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { LuArrowDown, LuArrowRight, LuInfo, LuUpload } from 'react-icons/lu'
import * as Yup from 'yup'
import { useUserStore } from '@/stores/user.store'
import { uploadOrderAttachment } from '@/api/order-attachments.api'
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENT_FILES,
  MAX_ATTACHMENT_FILE_SIZE,
  translateAttachmentError,
} from '@/common/attachments'
import { getErrorMessage } from '@/helper/apiError.helpers'
import {
  INVOICE_SETTINGS_ROUTE,
  ORDER_SUBMIT_FAILED_TITLE,
  isSellerTaxDataError,
  showSellerTaxDataToast,
} from '@/helper/sellerTaxData.helpers'
import { hoursToUnits, unitsToHours } from '@/helper/order.helper'
import { createOrder, getWorkload } from '@/api/orders.api'
import type { CreateOrderRequest } from '@/api/dto/order.dto'
import type { Bank } from '@/common/settings.common'
import type { WorkloadBucket } from '@/common/order.common'

export type Capacity = { toj: number; consulting: number; training: number; pilot: number }

export type UiCustomer = {
  id: number
  name: string
  name2?: string | null
  name3?: string | null
  city?: string
  active?: boolean
  // Not yet returned by the API for associated entities; included for future bank search support
  postal?: string | null
  blzNum?: string | null
  accountNumber?: string | null
}
export type OrderPayload = {
  year: number
  customerId: number
  // bankId: string      //default union investment
  fromMonth: number
  fromDay: number
  toMonth: number
  toDay: number
  toj: number
  consulting: number
  training: number
  pilot: number
}

export type OrderCreateDialogProps = {
  initialCapacity: Capacity
  banks: Bank[]
  customers: UiCustomer[]
  /** Customers are still being fetched — an empty list does not yet mean "no results". */
  customersLoading?: boolean
  /** The customer request failed; empty means we could not load them, not that none exist. */
  customersFailed?: boolean
  onSubmit: (data: OrderPayload) => Promise<
    | { ok: true }
    | {
        ok: false
        fieldErrors?: Partial<Record<keyof OrderPayload, string>>
        message?: string
      }
  >
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: React.ReactNode
  onOrderCreated?: () => void
  /** Opens the invoice settings; used when the SIB user's own tax data blocks the order. */
  onGoToInvoiceSettings?: () => void
}

const requiredMsg = 'Bitte füllen Sie dieses Feld korrekt aus.'
const numberField = Yup.number()
  .typeError(requiredMsg)
  // .integer('Bitte geben Sie eine ganze Zahl ein.')
  .min(0, 'Stunden müssen mindestens 0 sein.')
  .max(999, 'Maximal 999 erlaubt.')
  .test('half-step', 'Bitte nur 0,5-Schritte eingeben.', value => {
    if (value == null) return false
    return Number.isInteger(value * 2)
  })
  .required(requiredMsg)

// compute "today" once (supports Storybook mocks)
const today = new Date(Date.now())
const currentYear = today.getFullYear()
const nextYear = currentYear + 1

// build schema using this stable date
const pilotNumberField = Yup.number()
  .typeError(requiredMsg)
  .integer('Bitte nur ganze Zahlen eingeben.')
  .min(0, 'Stunden müssen mindestens 0 sein.')
  .max(999, 'Maximal 999 erlaubt.')
  .required(requiredMsg)

const schema = Yup.object({
  // Whether the year is open for planning comes from the backend (SIB-928), see yearSelectionBlocked.
  year: Yup.number().required(requiredMsg),
  customerId: Yup.number().min(1, requiredMsg).required(requiredMsg),
  fromMonth: Yup.number().min(1).max(12).required(requiredMsg),
  fromDay: Yup.number().min(1).max(31).required(requiredMsg),
  toMonth: Yup.number().min(1).max(12).required(requiredMsg),
  toDay: Yup.number().min(1).max(31).required(requiredMsg),
  toj: numberField,
  consulting: numberField,
  training: numberField,
  pilot: pilotNumberField,
}).test('date-validation', requiredMsg, values => {
  const { fromMonth, fromDay, toMonth, toDay, year } = values as any
  if (!fromMonth || !fromDay || !toMonth || !toDay || !year) return false

  const start = new Date(year, fromMonth - 1, fromDay)
  const end = new Date(year, toMonth - 1, toDay)

  // must be before
  if (end <= start) return false

  // same year only, within current or next
  if (![currentYear, nextYear].includes(year)) return false

  return true
})

function StatBox({
  label,
  capacity,
  entered,
}: {
  label: string
  capacity: number
  entered: number
}) {
  const hasEntered = entered !== 0 && entered !== undefined && entered !== null
  const remaining = capacity - entered
  const withinLimit = remaining >= 0

  return (
    <Stat.Root
      direction={{ base: 'row', md: 'column' }}
      align={{ base: 'center', md: 'flex-start' }}
      justifyContent="space-between"
      gap={3}
    >
      <HStack gap={3.5}>
        <Stat.Label>{label}</Stat.Label>
      </HStack>

      <HStack
        align="center"
        justifyContent={{ base: 'end', md: 'space-between' }}
        w="full"
        gap={2}
      >
        <Stat.ValueText>{hasEntered ? remaining : capacity}</Stat.ValueText>

        {/* Only show delta and arrow once user entered a value */}
        {hasEntered && (
          <Badge
            order={{ base: '-1', md: '1' }}
            colorPalette={withinLimit ? 'green' : 'red'}
            variant="subtle"
          >
            <LuArrowDown /> {entered}
          </Badge>
        )}
      </HStack>
    </Stat.Root>
  )
}
//Format bank display name to include all names separated with -
function formatBankDisplayName(c: UiCustomer) {
  return [c.name, c.name2, c.name3].filter(Boolean).join(' – ')
}

export default function OrderCreateDialog({
  initialCapacity,
  customers,
  customersLoading = false,
  customersFailed = false,
  isOpen,
  onOpenChange,
  trigger,
  onOrderCreated,
  onGoToInvoiceSettings,
}: OrderCreateDialogProps) {
  const today = useMemo(() => new Date(Date.now()), [])
  const years = [today.getFullYear(), today.getFullYear() + 1]
  const planningYears = usePlanningYears()
  const [submitting, setSubmitting] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [capacity, setCapacity] = useState<Capacity | null>(initialCapacity)
  const user = useUserStore(state => state.user)

  const quantityFields = ['toj', 'consulting', 'training'] as const

  const [quantityInputs, setQuantityInputs] = useState<
    Record<(typeof quantityFields)[number] | 'pilot', string>
  >({
    toj: '0',
    consulting: '0',
    training: '0',
    pilot: '0',
  })

  const formik = useFormik<OrderPayload>({
    validateOnBlur: true,
    validateOnChange: true,
    initialValues: {
      customerId: 0,
      year: years[0],
      // bankId: '', // Union Investment
      fromMonth: 1,
      fromDay: 1,
      toMonth: 12,
      toDay: 31,
      toj: 0,
      consulting: 0,
      training: 0,
      pilot: 0,
    },
    validationSchema: schema,
    onSubmit: async values => {
      setSubmitting(true)

      try {
        const startTime = new Date(
          Date.UTC(values.year, values.fromMonth - 1, values.fromDay, 0, 0, 0)
        ).toISOString()
        const endTime = new Date(
          Date.UTC(values.year, values.toMonth - 1, values.toDay, 23, 59, 59)
        ).toISOString()
        const tojUnits = Number(values.toj) || 0
        const consultingUnits = Number(values.consulting) || 0
        const trainingUnits = Number(values.training) || 0
        const pilotUnits = Number(values.pilot) || 0

        const buckets: CreateOrderRequest['buckets'] = [
          { serviceType: 'TOJ', plannedHours: unitsToHours('TOJ', tojUnits) },
          { serviceType: 'CONSULTING', plannedHours: unitsToHours('CONSULTING', consultingUnits) },
          { serviceType: 'TRAINING', plannedHours: unitsToHours('TRAINING', trainingUnits) },
          { serviceType: 'PILOT', plannedHours: unitsToHours('PILOT', pilotUnits) },
        ]

        const payload: CreateOrderRequest = {
          customerId: Number(values.customerId), // Union Investment - the customer
          bankId: 1, // Selected bank from the list
          startTime,
          endTime,
          buckets,
        }

        const created = await createOrder(payload)
        const orderId = created.id

        // 2. UPLOAD FILES
        if (files.length > 0) {
          for (const f of files) {
            try {
              await uploadOrderAttachment(orderId, f)
            } catch (e) {
              console.error('Failed to upload order attachment', e)
              toast.error(
                'Upload fehlgeschlagen',
                `${f.name} konnte nicht hochgeladen werden: ${translateAttachmentError(
                  getErrorMessage(e, 'Unbekannter Fehler')
                )}`,
                { duration: 8000 }
              )
            }
          }
        }
        onOrderCreated?.()

        toast.success(
          'Bestellung wurde übermittelt',
          'Den Bearbeitungsstatus finden Sie im Dashboard unter „Bestellungen“.'
        )

        onOpenChange?.(false)
      } catch (error) {
        // An approved order produces a commission invoice issued in the SIB user's name, so the
        // backend refuses the order while their tax data could not be on that invoice.
        if (isSellerTaxDataError(error)) {
          showSellerTaxDataToast(
            () => {
              onOpenChange?.(false)
              if (onGoToInvoiceSettings) onGoToInvoiceSettings()
              else window.location.assign(INVOICE_SETTINGS_ROUTE)
            },
            ORDER_SUBMIT_FAILED_TITLE,
            error
          )
          return
        }
        console.error('Failed to create order', error)
        toast.error(
          ORDER_SUBMIT_FAILED_TITLE,
          getErrorMessage(error, 'Bitte führen Sie den Vorgang erneut aus.')
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  // const selectedCustomer = useMemo(
  //   () => banks.find(b => Number(b.id) === Number(formik.values.customerId)),
  //   [banks, formik.values.customerId]
  // )

  // const associatedBanks: UiCustomer[] = useMemo(() => customers, [customers])
  const associatedBanks: UiCustomer[] = useMemo(
    () => customers.filter(c => c.active !== false),
    [customers]
  )

  const selectedCustomer = useMemo(
    () => associatedBanks.find(c => Number(c.id) === Number(formik.values.customerId)),
    [associatedBanks, formik.values.customerId]
  )

  const isUnionRecipient = useMemo(
    () =>
      !!selectedCustomer &&
      formatBankDisplayName(selectedCustomer).toLowerCase().includes('union'),
    [selectedCustomer]
  )

  const [inputValue, setInputValue] = useState('')

  const bankCollection = useMemo(() => {
    const query = inputValue.trim().toLowerCase()

    const filteredBanks = query
      ? associatedBanks.filter(c => {
          const label = formatBankDisplayName(c).toLowerCase()
          const postal = (c.postal ?? '').toLowerCase()
          const blzNum = (c.blzNum ?? '').toLowerCase()
          const accountNumber = (c.accountNumber ?? '').toLowerCase()

          return (
            label.includes(query) ||
            postal.includes(query) ||
            blzNum.includes(query) ||
            accountNumber.includes(query)
          )
        })
      : associatedBanks

    const items = filteredBanks.map(c => ({
      label: formatBankDisplayName(c),
      value: String(c.id),
    }))

    return createListCollection({ items })
  }, [associatedBanks, inputValue])

  useEffect(() => {
    if (!associatedBanks.length) {
      formik.setFieldValue('customerId', 0)
      return
    }

    const current = formik.values.customerId
    const stillExists = associatedBanks.some(c => Number(c.id) === Number(current))
    if (!stillExists) formik.setFieldValue('customerId', Number(associatedBanks[0].id))
  }, [associatedBanks])

  useEffect(() => {
    async function load() {
      if (!user?.id || !formik.values.year) {
        return
      }

      const request = {
        userId: user.id,
        from: `${formik.values.year}-01-01T00:00:00`,
        to: `${formik.values.year}-12-31T23:59:59`,
      }

      try {
        const result = await getWorkload(request)

        const buckets = result.buckets ?? []

        const tojHours = buckets.find(
          (b: WorkloadBucket) => b.serviceType === 'TOJ'
        )?.remainingCapacityHours

        const consultingHours = buckets.find(
          (b: WorkloadBucket) => b.serviceType === 'CONSULTING'
        )?.remainingCapacityHours

        const trainingHours = buckets.find(
          (b: WorkloadBucket) => b.serviceType === 'TRAINING'
        )?.remainingCapacityHours

        const pilotHours = buckets.find(
          (b: WorkloadBucket) => b.serviceType === 'PILOT'
        )?.remainingCapacityHours

        setCapacity({
          toj: hoursToUnits('TOJ', tojHours || 0),
          consulting: hoursToUnits('CONSULTING', consultingHours || 0),
          training: hoursToUnits('TRAINING', trainingHours || 0),
          pilot: hoursToUnits('PILOT', pilotHours || 0),
        })
      } catch (err) {
        console.error('[OrderCreateDialog] workload error:', err)
        toast.error('Kapazitätsdaten konnten nicht geladen werden')
      }
    }

    load()
  }, [user?.id, formik.values.year])

  useEffect(() => {
    if (!isUnionRecipient && formik.values.pilot !== 0) {
      formik.setFieldValue('pilot', 0, false)
      setQuantityInputs(prev => ({ ...prev, pilot: '0' }))
    }
  }, [isUnionRecipient])

  useEffect(() => {
    if (isOpen) {
      formik.resetForm()
      setFiles([]) // reset attachments also
      setInputValue('') //reset combobox
    }
  }, [isOpen])

  useEffect(() => {
    setQuantityInputs({
      toj: String(formik.values.toj),
      consulting: String(formik.values.consulting),
      training: String(formik.values.training),
      pilot: String(formik.values.pilot),
    })
  }, [isOpen])
  const entered = {
    toj: formik.values.toj,
    beratung: formik.values.consulting,
    schulung: formik.values.training,
    pilot: formik.values.pilot,
  }

  const exceeds =
    entered.toj > initialCapacity.toj ||
    entered.beratung > initialCapacity.consulting ||
    entered.schulung > initialCapacity.training ||
    (isUnionRecipient && entered.pilot > (initialCapacity.pilot ?? 0))

  // Returns the correct number of days per month, including leap years
  function getDaysInMonth(month: number, year: number) {
    return new Date(year, month, 0).getDate() // 0 -> last day of previous month
  }
  const daysInFromMonth = getDaysInMonth(formik.values.fromMonth, formik.values.year)
  const daysInToMonth = getDaysInMonth(formik.values.toMonth, formik.values.year)

  const yearSelectionBlocked = !planningYears.isOpen(formik.values.year)

  const allRequiredFilled =
    !!formik.values.customerId &&
    (formik.values.toj > 0 ||
      formik.values.consulting > 0 ||
      formik.values.training > 0 ||
      (isUnionRecipient && formik.values.pilot > 0)) &&
    formik.values.fromMonth &&
    formik.values.fromDay &&
    formik.values.toMonth &&
    formik.values.toDay

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={e => onOpenChange?.(e.open)}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner zIndex={'modal'}>
            <Dialog.Content maxW={{ base: '90vw', md: '3xl' }} maxH="75dvh" overflow="hidden">
              {/* <VStack align="stretch" gap={4} w="full"> */}
              {/* Header */}
              <Dialog.Header pointerEvents="all">
                <DialogTitle>Neue Bestellung</DialogTitle>
                <Dialog.CloseTrigger asChild>
                  <CloseButton />
                </Dialog.CloseTrigger>
              </Dialog.Header>

              <form onSubmit={formik.handleSubmit} style={{ display: 'contents' }}>
                <Dialog.Body pointerEvents="all" flex={1} overflow="hidden">
                  <Flex
                    direction="column"
                    overflow="auto"
                    css={{
                      scrollbarGutter: 'stable',
                      '@media (max-width: 767px)': {
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                          display: 'none',
                        },
                      },
                    }}
                  >
                    <Flex direction="column" gap={4} p={{ base: 1, md: 4 }}>
                      {/* Year selector + capacity stats */}
                      <Flex
                        w="full"
                        justify="space-between"
                        align="flex-start"
                        gap={4}
                        flexWrap="wrap"
                      >
                        <VStack align="flex-start" gap={2}>
                          <FieldRoot>
                            <FieldLabel>Kalenderjahr</FieldLabel>
                          </FieldRoot>
                          <HStack gap={0}>
                            {years.map(y => {
                              const disabled = !planningYears.isOpen(y)
                              const unlockedFrom = planningYears.unlockedFrom(y)
                              const button = (
                                <Button
                                  key={y}
                                  variant={formik.values.year === y ? 'subtle' : 'ghost'}
                                  colorPalette="gray"
                                  disabled={disabled}
                                  onClick={e => {
                                    if (disabled) {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      return
                                    }
                                    formik.setFieldValue('year', y, true)
                                  }}
                                >
                                  {y}
                                </Button>
                              )

                              return disabled ? (
                                <Tooltip
                                  key={y}
                                  content={
                                    !planningYears.loaded
                                      ? `Das Kalenderjahr ${y} wird geladen …`
                                      : unlockedFrom
                                        ? `Das Kalenderjahr ${y} ist ab dem ${formatIsoDateDE(unlockedFrom)} verfügbar.`
                                        : `Das Kalenderjahr ${y} ist noch nicht für die Planung freigegeben.`
                                  }
                                  openDelay={300}
                                >
                                  {button}
                                </Tooltip>
                              ) : (
                                button
                              )
                            })}
                          </HStack>
                        </VStack>

                        {/* Stats */}
                        <Flex direction="column" gap="4">
                          <Box>
                            <Text>
                              Verbleibendes Leistungskontingent für das ausgewählte Kalenderjahr
                            </Text>
                          </Box>
                          <Stack
                            direction={{ base: 'column', md: 'row' }}
                            justifyContent={'space-between'}
                          >
                            <Card.Root flex={1}>
                              <Card.Body>
                                <StatBox
                                  label="TOJ"
                                  capacity={capacity?.toj ?? 0}
                                  entered={formik.values.toj}
                                />
                              </Card.Body>
                            </Card.Root>
                            <Card.Root flex={1}>
                              <Card.Body>
                                <StatBox
                                  label="Beratung"
                                  capacity={capacity?.consulting ?? 0}
                                  entered={formik.values.consulting}
                                />
                              </Card.Body>
                            </Card.Root>
                            <Card.Root flex={1}>
                              <Card.Body>
                                <StatBox
                                  label="Schulung"
                                  capacity={capacity?.training ?? 0}
                                  entered={formik.values.training}
                                />
                              </Card.Body>
                            </Card.Root>
                            {isUnionRecipient && (
                              <Card.Root flex={1}>
                                <Card.Body>
                                  <StatBox
                                    label="Pilot"
                                    capacity={capacity?.pilot ?? 0}
                                    entered={formik.values.pilot}
                                  />
                                </Card.Body>
                              </Card.Root>
                            )}
                          </Stack>
                        </Flex>
                      </Flex>

                      {/* Divider */}
                      <Separator />

                      {/* Form Fields */}
                      <VStack align="stretch" gap={4}>
                        <Stack
                          direction={{ base: 'column', md: 'row' }}
                          align="flex-start"
                          gap={4}
                        >
                          {/* Auftraggeber (Union Investment - fixed) */}
                          <FieldRoot invalid={!!formik.errors.customerId}>
                            <FieldLabel>Auftraggeber</FieldLabel>
                            <Input value="Union Investment" disabled />
                          </FieldRoot>

                          {/* Leistungsempfänger (Banks - selectable). The FieldRoot provides the
                              context Field.RequiredIndicator needs; without it the dialog crashes. */}
                          <FieldRoot
                            invalid={!!formik.touched.customerId && !!formik.errors.customerId}
                          >
                            <Combobox.Root
                              invalid={!!formik.errors.customerId}
                              collection={bankCollection}
                              onInputValueChange={e => setInputValue(e.inputValue)}
                              value={
                                formik.values.customerId ? [String(formik.values.customerId)] : []
                              }
                              onValueChange={v =>
                                formik.setFieldValue('customerId', Number(v.value[0]))
                              }
                              placeholder="Suchen..."
                            >
                              <Combobox.Label>
                                Leistungsempfänger <Field.RequiredIndicator />
                              </Combobox.Label>
                              <Combobox.Control>
                                {/* Deliberately not disabled while loading: disabling the input
                                  stops the list opening, which would make the loading message
                                  in Combobox.Empty unreachable — the two would cancel out. */}
                                <Combobox.Input
                                  placeholder={customersLoading ? 'Wird geladen …' : 'Suchen...'}
                                />
                                <Combobox.IndicatorGroup>
                                  <Combobox.ClearTrigger
                                    onClick={() => {
                                      formik.setFieldValue('customerId', 0)
                                    }}
                                  />
                                  <Combobox.Trigger />
                                </Combobox.IndicatorGroup>
                              </Combobox.Control>
                              <Portal>
                                <Combobox.Positioner style={{ zIndex: 10000 }} zIndex={10000}>
                                  <Combobox.Content>
                                    {/* "Keine gefunden" is only true once the list has actually
                                      arrived. Saying it while the request is still in flight —
                                      or after it failed — is what made this read as a bug. */}
                                    <Combobox.Empty>
                                      {customersLoading
                                        ? 'Leistungsempfänger werden geladen …'
                                        : customersFailed
                                          ? 'Leistungsempfänger konnten nicht geladen werden. Bitte laden Sie die Seite neu.'
                                          : 'Keine Leistungsempfänger gefunden'}
                                    </Combobox.Empty>
                                    {bankCollection.items.map(item => (
                                      <Combobox.Item item={item} key={item.value}>
                                        {item.label}
                                        <Combobox.ItemIndicator />
                                      </Combobox.Item>
                                    ))}
                                  </Combobox.Content>
                                </Combobox.Positioner>
                              </Portal>
                            </Combobox.Root>
                            <FieldErrorText>
                              {formik.touched.customerId && formik.errors.customerId}
                            </FieldErrorText>
                          </FieldRoot>
                        </Stack>
                        {/* Zeitraum section */}
                        <Stack
                          direction={{ base: 'column', md: 'row' }}
                          align="flex-start"
                          gap={4}
                          w="full"
                        >
                          {/* Zeitraum ab */}
                          <VStack align="flex-start" gap={1} flex={1} w="full">
                            <FieldRoot w="full" display="flex" flexDirection="column">
                              <FieldLabel>
                                Zeitraum ab <Field.RequiredIndicator />
                              </FieldLabel>

                              <HStack w="full" gap={2}>
                                {/* Monat */}
                                <Box flex={{ base: 2, md: 1 }} minW={0}>
                                  <SelectRoot
                                    collection={createListCollection({
                                      items: Array.from({ length: 12 }, (_, i) => ({
                                        label: new Date(0, i).toLocaleString('de-DE', {
                                          month: 'long',
                                        }),
                                        value: String(i + 1),
                                      })),
                                    })}
                                    value={[String(formik.values.fromMonth)]}
                                    onValueChange={v =>
                                      formik.setFieldValue('fromMonth', Number(v.value[0]))
                                    }
                                  >
                                    <SelectControl flex={2} minW={0}>
                                      <SelectTrigger w="full">
                                        <SelectValueText placeholder="Monat" />
                                        <SelectIndicatorGroup>
                                          <SelectIndicator />
                                        </SelectIndicatorGroup>
                                      </SelectTrigger>
                                    </SelectControl>
                                    <Portal>
                                      <SelectPositioner style={{ zIndex: 10000 }}>
                                        <SelectContent>
                                          {Array.from({ length: 12 }, (_, i) => (
                                            <SelectItem key={i} item={String(i + 1)}>
                                              {new Date(0, i).toLocaleString('de-DE', {
                                                month: 'long',
                                              })}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </SelectPositioner>
                                    </Portal>
                                  </SelectRoot>
                                </Box>

                                {/* Tag */}
                                <Box flex={1} minW={0}>
                                  <SelectRoot
                                    collection={createListCollection({
                                      items: Array.from({ length: 31 }, (_, i) => ({
                                        label: String(i + 1),
                                        value: String(i + 1),
                                      })),
                                    })}
                                    value={[String(formik.values.fromDay)]}
                                    onValueChange={v =>
                                      formik.setFieldValue('fromDay', Number(v.value[0]))
                                    }
                                  >
                                    <SelectControl>
                                      <SelectTrigger w="full">
                                        <SelectValueText placeholder="Tag" />
                                        <SelectIndicatorGroup>
                                          <SelectIndicator />
                                        </SelectIndicatorGroup>
                                      </SelectTrigger>
                                    </SelectControl>

                                    <Portal>
                                      <SelectPositioner style={{ zIndex: 10000 }}>
                                        <SelectContent>
                                          {Array.from({ length: daysInFromMonth }, (_, i) => (
                                            <SelectItem key={i} item={String(i + 1)}>
                                              {i + 1}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </SelectPositioner>
                                    </Portal>
                                  </SelectRoot>
                                </Box>
                              </HStack>

                              <FieldErrorText>
                                {(formik.touched.fromMonth || formik.touched.fromDay) &&
                                  (formik.errors.fromMonth || formik.errors.fromDay)}
                              </FieldErrorText>
                            </FieldRoot>
                          </VStack>

                          {/* Zeitraum bis */}
                          <VStack align="flex-start" gap={1} flex={1} w="full">
                            <FieldRoot w="full" display="flex" flexDirection="column">
                              <FieldLabel>
                                Zeitraum bis <Field.RequiredIndicator />
                              </FieldLabel>
                              <HStack gap={2} w="full">
                                {/* Monat */}
                                <Box flex={{ base: 2, md: 1 }} minW={0}>
                                  <SelectRoot
                                    collection={createListCollection({
                                      items: Array.from({ length: 12 }, (_, i) => ({
                                        label: new Date(0, i).toLocaleString('de-DE', {
                                          month: 'long',
                                        }),
                                        value: String(i + 1),
                                      })),
                                    })}
                                    value={[String(formik.values.toMonth)]}
                                    onValueChange={v =>
                                      formik.setFieldValue('toMonth', Number(v.value[0]))
                                    }
                                  >
                                    <SelectControl>
                                      <SelectTrigger w="full">
                                        <SelectValueText placeholder="Monat" />
                                        <SelectIndicatorGroup>
                                          <SelectIndicator />
                                          {/* <SelectClearTrigger /> */}
                                        </SelectIndicatorGroup>
                                      </SelectTrigger>
                                    </SelectControl>
                                    <Portal>
                                      <SelectPositioner style={{ zIndex: 10000 }}>
                                        <SelectContent>
                                          {Array.from({ length: 12 }, (_, i) => {
                                            const month = i + 1
                                            const isDisabled = month < formik.values.fromMonth

                                            return (
                                              <SelectItem
                                                key={month}
                                                item={String(month)}
                                                data-disabled={isDisabled || undefined}
                                                _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                                              >
                                                {new Date(0, i).toLocaleString('de-DE', {
                                                  month: 'long',
                                                })}
                                              </SelectItem>
                                            )
                                          })}
                                        </SelectContent>
                                      </SelectPositioner>
                                    </Portal>
                                  </SelectRoot>
                                </Box>

                                {/* Tag */}
                                <Box flex={1} minW={0}>
                                  <SelectRoot
                                    collection={createListCollection({
                                      items: Array.from({ length: 31 }, (_, i) => ({
                                        label: String(i + 1),
                                        value: String(i + 1),
                                      })),
                                    })}
                                    value={[String(formik.values.toDay)]}
                                    onValueChange={v =>
                                      formik.setFieldValue('toDay', Number(v.value[0]))
                                    }
                                  >
                                    <SelectControl>
                                      <SelectTrigger w="full">
                                        <SelectValueText placeholder="Tag" />
                                        <SelectIndicatorGroup>
                                          <SelectIndicator />
                                          {/* <SelectClearTrigger /> */}
                                        </SelectIndicatorGroup>
                                      </SelectTrigger>
                                    </SelectControl>
                                    <Portal>
                                      <SelectPositioner style={{ zIndex: 10000 }}>
                                        <SelectContent>
                                          {Array.from({ length: daysInToMonth }, (_, i) => {
                                            const day = i + 1
                                            const isDisabled =
                                              formik.values.toMonth === formik.values.fromMonth &&
                                              day < formik.values.fromDay

                                            return (
                                              <SelectItem
                                                key={day}
                                                item={String(day)}
                                                data-disabled={isDisabled || undefined}
                                                _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                                              >
                                                {day}
                                              </SelectItem>
                                            )
                                          })}
                                        </SelectContent>
                                      </SelectPositioner>
                                    </Portal>
                                  </SelectRoot>
                                </Box>
                              </HStack>
                              <FieldErrorText>
                                {(formik.touched.toMonth || formik.touched.toDay) &&
                                  (formik.errors.toMonth || formik.errors.toDay)}
                              </FieldErrorText>
                            </FieldRoot>
                          </VStack>
                        </Stack>

                        <HStack align="flex-start" gap={4}>
                          {quantityFields.map(field => (
                            <FieldRoot key={field} invalid={!!formik.errors[field]}>
                              <FieldLabel textTransform="capitalize">
                                {field === 'toj'
                                  ? 'TOJ'
                                  : field === 'consulting'
                                    ? 'Beratung'
                                    : 'Schulung'}
                                <Field.RequiredIndicator />
                              </FieldLabel>

                              <Input
                                type="text"
                                inputMode="decimal"
                                value={quantityInputs[field]}
                                onFocus={e => e.target.select()}
                                onChange={e => {
                                  const raw = e.target.value.replace(',', '.')

                                  if (!/^\d{0,3}(\.\d{0,1})?$/.test(raw)) return

                                  setQuantityInputs(prev => ({
                                    ...prev,
                                    [field]: raw,
                                  }))
                                }}
                                onBlur={() => {
                                  const raw = quantityInputs[field]

                                  if (raw === '' || raw === '.') {
                                    formik.setFieldValue(field, 0)
                                    setQuantityInputs(prev => ({
                                      ...prev,
                                      [field]: '0',
                                    }))
                                    return
                                  }

                                  const parsed = Number(raw)

                                  if (
                                    Number.isNaN(parsed) ||
                                    parsed < 0 ||
                                    parsed > 999 ||
                                    !Number.isInteger(parsed * 2)
                                  ) {
                                    setQuantityInputs(prev => ({
                                      ...prev,
                                      [field]: String(formik.values[field]),
                                    }))
                                    return
                                  }

                                  formik.setFieldValue(field, parsed)
                                  setQuantityInputs(prev => ({
                                    ...prev,
                                    [field]: String(parsed),
                                  }))
                                }}
                              />

                              <FieldHelperText>
                                {field === 'toj'
                                  ? '1 Einheit = 2,5 h'
                                  : field === 'consulting'
                                    ? '1 Einheit = 1 h'
                                    : '1 Einheit = 8 h'}
                              </FieldHelperText>

                              <FieldErrorText>
                                {formik.touched[field] && formik.errors[field]}
                              </FieldErrorText>
                            </FieldRoot>
                          ))}

                          {isUnionRecipient && (
                            <FieldRoot invalid={!!formik.errors.pilot}>
                              <FieldLabel>
                                Pilot <Field.RequiredIndicator />
                              </FieldLabel>

                              <Input
                                type="text"
                                inputMode="numeric"
                                value={quantityInputs.pilot}
                                onFocus={e => e.target.select()}
                                onChange={e => {
                                  const raw = e.target.value.replace(',', '.')
                                  if (!/^\d{0,3}$/.test(raw)) return
                                  setQuantityInputs(prev => ({ ...prev, pilot: raw }))
                                }}
                                onBlur={() => {
                                  const raw = quantityInputs.pilot
                                  if (raw === '') {
                                    formik.setFieldValue('pilot', 0)
                                    setQuantityInputs(prev => ({ ...prev, pilot: '0' }))
                                    return
                                  }
                                  const parsed = Number(raw)
                                  if (
                                    Number.isNaN(parsed) ||
                                    parsed < 0 ||
                                    parsed > 999 ||
                                    !Number.isInteger(parsed)
                                  ) {
                                    setQuantityInputs(prev => ({
                                      ...prev,
                                      pilot: String(formik.values.pilot),
                                    }))
                                    return
                                  }
                                  formik.setFieldValue('pilot', parsed)
                                  setQuantityInputs(prev => ({ ...prev, pilot: String(parsed) }))
                                }}
                              />

                              <FieldHelperText>1 Einheit = 1 h, nur ganze Zahlen</FieldHelperText>

                              <FieldErrorText>
                                {formik.touched.pilot && formik.errors.pilot}
                              </FieldErrorText>
                            </FieldRoot>
                          )}
                        </HStack>
                      </VStack>
                      {exceeds && (
                        <Alert.Root status="warning">
                          <Alert.Indicator />
                          <Alert.Content>
                            <Alert.Title>Achtung</Alert.Title>
                            <Alert.Description>
                              Vermittelte Leistungen reichen über das vereinbarte
                              Leistungskontingent hinaus. Dies könnte ein Ablehnungsgrund für die
                              Union Investment darstellen.
                            </Alert.Description>
                          </Alert.Content>
                        </Alert.Root>
                      )}
                      {/* --- Attachments Section --- */}
                      <Box mt={4}>
                        <FileUpload.Root
                          maxFiles={MAX_ATTACHMENT_FILES}
                          maxFileSize={MAX_ATTACHMENT_FILE_SIZE}
                          accept={ATTACHMENT_ACCEPT}
                          onFileAccept={e => setFiles(e.files)}
                          onFileReject={e => {
                            const rejectedNames = e.files.map(f => f.file.name).join(', ')
                            toast.error(
                              'Ungültige Datei',
                              `Die folgenden Dateien werden nicht unterstützt: ${rejectedNames}`
                            )
                          }}
                        >
                          <FileUpload.Label>Anhänge</FileUpload.Label>
                          <FileUpload.HiddenInput multiple />

                          <FileUpload.Trigger asChild>
                            <Button variant="outline">
                              <LuUpload /> Anhang hinzufügen
                            </Button>
                          </FileUpload.Trigger>

                          {files.length > 0 && (
                            <Box w="full" alignSelf="stretch" mt={3}>
                              <FileUpload.Label>Anhänge verwalten</FileUpload.Label>
                              <FileUpload.List showSize clearable />
                            </Box>
                          )}
                        </FileUpload.Root>

                        {files.length > 0 && (
                          <HStack align="center" gap={2} mt={2}>
                            <LuInfo />
                            <Text>
                              Sie können bis zu {MAX_ATTACHMENT_FILES} Anhänge mit einer Größe von
                              jeweils bis zu {Math.round(MAX_ATTACHMENT_FILE_SIZE / (1024 * 1024))}{' '}
                              MB hochladen.
                            </Text>
                          </HStack>
                        )}
                      </Box>
                    </Flex>
                  </Flex>
                </Dialog.Body>

                {/* Footer */}
                <Dialog.Footer pointerEvents="all">
                  <Flex justify="flex-end" w="full" gap={4}>
                    <Button
                      type="submit"
                      loading={submitting}
                      disabled={
                        !allRequiredFilled || !formik.isValid || submitting || yearSelectionBlocked
                      }
                      variant="solid"
                      colorPalette="gray"
                    >
                      {submitting ? 'Wird übermittelt…' : 'Bestellung übermitteln'}{' '}
                      <LuArrowRight />
                    </Button>
                  </Flex>
                </Dialog.Footer>
              </form>
              {/* </VStack> */}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}
